import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import useWebSocketMessage from './useWebSocketMessage.js';
import { type ActivityEvent, usePendingActivityEvents } from '../../api/activity.api.js';
import { WebSocketMessageTypes } from '../../models/index.js';
import {
  activityEventPushed,
  activityEventRemoved,
  activityEventsHydrated,
} from '../../redux/reducers/pendingActivityEvents.js';

/**
 * Streams-page side-effect bundle: hydrates the pending-activity-events slice
 * from the REST endpoint on mount (all pending events in the last 30 min
 * across all visible patients, scoped by Bedrock-event read permission), then
 * layers WS pushes on top for real-time arrivals + review dismissals.
 */
export function usePendingActivityEventsSync(
  visiblePatientIds: string[],
  enabled = true,
): void {
  const dispatch = useDispatch();
  const created = useWebSocketMessage<ActivityEvent>(WebSocketMessageTypes.ACTIVITY_EVENT_CREATED);
  const reviewed = useWebSocketMessage<{ id: string }>(WebSocketMessageTypes.ACTIVITY_EVENT_REVIEWED);

  const window = useMemo(
    () => ({
      from: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      to: new Date().toISOString(),
    }),
    [],
  );

  const hydrate = usePendingActivityEvents(
    { from: window.from, to: window.to, reviewStatus: 'pending' },
    { enabled: enabled && visiblePatientIds.length > 0, refetchOnWindowFocus: false },
  );

  // `enabled` is the caller's activity-event read permission. When false we
  // neither hydrate nor react to WS pushes, so the store stays empty for users
  // (e.g. monitors) who must not see any AI affordances.
  useEffect(() => {
    if (!enabled || !hydrate.data) return;
    const visibleSet = new Set(visiblePatientIds);
    const filtered = visiblePatientIds.length > 0
      ? hydrate.data.items.filter((e) => visibleSet.has(e.patientId))
      : hydrate.data.items;
    dispatch(activityEventsHydrated(filtered));
  }, [enabled, hydrate.data, visiblePatientIds, dispatch]);

  useEffect(() => {
    if (enabled && created?.data) dispatch(activityEventPushed(created.data));
  }, [enabled, created, dispatch]);

  useEffect(() => {
    if (enabled && reviewed?.data?.id) dispatch(activityEventRemoved(reviewed.data.id));
  }, [enabled, reviewed, dispatch]);
}
